import { BookOpen, ExternalLink, Globe, Star, Wrench } from "lucide-react";
import React, { useState } from "react";
import { recommendations } from "../data/content";
import { Card, CardContent } from "./ui/card";

// Individual recommendation card component
const RecommendationCard = ({ item, type }) => {
  const getIcon = () => {
    switch (type) {
      case "books":
        return <BookOpen className="w-5 h-5" />;
      case "tools":
        return <Wrench className="w-5 h-5" />;
      case "resources":
        return <Globe className="w-5 h-5" />;
      default:
        return <Star className="w-5 h-5" />;
    }
  };

  const getGradient = () => {
    switch (type) {
      case "books":
        return "from-green-50 to-green-100";
      case "tools":
        return "from-blue-50 to-blue-100";
      case "resources":
        return "from-purple-50 to-purple-100";
      default:
        return "from-gray-50 to-gray-100";
    }
  };

  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:-translate-y-1 transition-transform duration-300">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={`p-2 rounded-lg bg-gradient-to-b ${getGradient()}`}
              >
                {getIcon()}
              </div>
              <div>
                <h3 className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {item.title || item.name}
                </h3>
                {item.author && (
                  <p className="text-sm text-gray-600">by {item.author}</p>
                )}
              </div>
            </div>
            {item.featured && (
              <span className="bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                Featured
              </span>
            )}
          </div>

          {/* Category and Rating */}
          <div className="flex items-center gap-4">
            <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
              {item.category}
            </span>
            {item.rating && (
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < item.rating
                        ? "text-yellow-500 fill-current"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Description/Notes */}
          <p className="text-gray-700 leading-relaxed text-sm">
            {item.notes || item.description}
          </p>

          {/* Why section for tools/resources */}
          {item.why && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-100 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Why I recommend it:</span>{" "}
                {item.why}
              </p>
            </div>
          )}

          {/* Link */}
          {item.link && (
            <div className="pt-2">
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
              >
                View{" "}
                {type === "books"
                  ? "Book"
                  : type === "tools"
                  ? "Tool"
                  : "Resource"}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Section component for each recommendation type
const RecommendationSection = ({ title, items, type, icon }) => {
  const [showAll, setShowAll] = useState(false);
  const displayItems = showAll ? items : items.slice(0, 4);

  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        {icon}
        <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
          {title}
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {displayItems.map((item) => (
          <RecommendationCard key={item.id} item={item} type={type} />
        ))}
      </div>

      {items.length > 4 && (
        <div className="text-center mt-6">
          <button
            onClick={() => setShowAll(!showAll)}
            className="bg-gradient-to-r from-blue-50 to-purple-100 text-blue-700 px-6 py-2 rounded-lg hover:scale-105 transition-transform duration-300 font-medium"
          >
            {showAll ? "Show Less" : `Show All ${items.length} ${title}`}
          </button>
        </div>
      )}
    </section>
  );
};

// Main recommendations page component
const RecommendationsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filterByCategory = (items, category) => {
    if (category === "all") return items;
    return items.filter((item) =>
      item.category.toLowerCase().includes(category.toLowerCase())
    );
  };

  const categories = [
    "all",
    "AI Safety",
    "Infrastructure",
    "Urban Planning",
    "Technology",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="py-16 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">
            Recommendations
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Books, tools, and resources I find valuable and worth sharing
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700"
                    : "bg-white/60 text-gray-600 hover:bg-white/80"
                }`}
              >
                {category === "all" ? "All Categories" : category}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pb-20">
        {/* Featured Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Star className="w-6 h-6 text-yellow-500" />
            <h2 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Featured Recommendations
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Featured books */}
            {recommendations.books
              .filter((book) => book.featured)
              .map((book) => (
                <RecommendationCard key={book.id} item={book} type="books" />
              ))}
            {/* Featured tools */}
            {recommendations.tools
              .filter((tool) => tool.featured)
              .map((tool) => (
                <RecommendationCard key={tool.id} item={tool} type="tools" />
              ))}
            {/* Featured resources */}
            {recommendations.resources
              .filter((resource) => resource.featured)
              .map((resource) => (
                <RecommendationCard
                  key={resource.id}
                  item={resource}
                  type="resources"
                />
              ))}
          </div>
        </section>

        {/* Books Section */}
        <RecommendationSection
          title="Books"
          items={filterByCategory(recommendations.books, selectedCategory)}
          type="books"
          icon={<BookOpen className="w-6 h-6 text-green-600" />}
        />

        {/* Tools Section */}
        <RecommendationSection
          title="Tools & Software"
          items={filterByCategory(recommendations.tools, selectedCategory)}
          type="tools"
          icon={<Wrench className="w-6 h-6 text-blue-600" />}
        />

        {/* Resources Section */}
        <RecommendationSection
          title="Resources"
          items={filterByCategory(recommendations.resources, selectedCategory)}
          type="resources"
          icon={<Globe className="w-6 h-6 text-purple-600" />}
        />
      </main>
    </div>
  );
};

export default RecommendationsPage;
